// @waypoint/contracts/backstop — the pattern backstop (E01-S2-T8; D-86, D-96, D-100). Perception runs
// it before a frame is emitted and the context firewall before a model call: does a string that is
// about to pass through look like something that must not? It reports which pattern hit and nothing
// about the value. Runs in a content script: no Node APIs, no dependencies, no zod.
//
// Bounded time, decided without a clock. Patterns are compiled once, and come in two kinds (T0, D-100):
//   - trusted: the built-ins (BUILTIN_PATTERNS, run by perception and the firewall) and the
//     code-owned patterns a service of this repository adds for itself (CODE_OWNED_PATTERNS, such as
//     the foundry intake's money formats). They are written in code, may use lookbehind and
//     backreferences, are vetted once in CI by a reference backtracking matcher that counts its steps
//     on worst-case inputs, and are trusted at run time only on a match by id AND source — so a pack
//     cannot claim one by reusing its id;
//   - pack-supplied: every other pattern (a pack, an atlas, a tenant). It must be written in the pack
//     subset (analyse.ts: no lookaround, backreference, nesting or unbounded quantifier, and a step
//     bound from its own size); anything else is refused at load and at pack build, failing closed. The same source
// gets the same answer on every machine: a busy page can no longer refuse a good pattern, which the
// wall-clock probe this replaces did. scan then reads a long value in overlapping windows, so its
// cost is linear in the value.
export { BUILTIN_PATTERNS, CODE_OWNED_PATTERNS } from "./patterns.js";
export { checkPackPattern, PACK_MAX_ALTERNATIVES, PACK_MAX_REPEAT, PACK_MAX_SOURCE, PACK_STEP_BUDGET, } from "./analyse.js";
import { checkPackPattern } from "./analyse.js";
import { BUILTIN_PATTERNS, CODE_OWNED_PATTERNS } from "./patterns.js";
/** The longest slice of a value one regular expression is run on. */
export const SCAN_WINDOW = 8192;
/**
 * Consecutive windows overlap by this much, less up to 128 characters moved to keep a digit run whole
 * (windows below), so a match up to 384 characters long is never split.
 */
export const SCAN_OVERLAP = 512;
const MAX_SOURCE = 1024;
const ID_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
/** How a subset rule is reported: too costly, or a construct the subset does not take at all. */
const REFUSAL = {
    invalid: "invalid",
    "too-long": "too-long",
    "nested-quantifier": "nested-quantifier",
    unbounded: "slow",
    wide: "slow",
    alternation: "slow",
    "zero-length": "matches-empty",
    steps: "slow",
    backreference: "unsupported",
    lookaround: "unsupported",
    "nested-group": "unsupported",
    "empty-alternative": "unsupported",
};
/** A pattern was refused at load. Names the pattern's id and the reason, never a value. */
export class BackstopPatternError extends Error {
    patternId;
    reason;
    constructor(patternId, reason) {
        super(`backstop pattern ${JSON.stringify(patternId)} refused: ${reason}`);
        this.name = "BackstopPatternError";
        this.patternId = patternId;
        this.reason = reason;
    }
}
/**
 * Does the source put an unbounded quantifier (+, *, {n,}) on a group that itself contains one?
 * The classic catastrophic shapes: (a+)+, (a*)*, (\w+\s?)+, (?:x|y+)*. A bounded outer quantifier,
 * or an unbounded inner one under a bounded outer, is left to checkPackPattern.
 */
export function hasNestedQuantifier(src) {
    const stack = [];
    let inner = false; // the current group contains an unbounded quantifier
    let i = 0;
    const quantifier = (at) => {
        const c = src[at];
        let end;
        let unbounded;
        if (c === "+" || c === "*") {
            unbounded = true;
            end = at + 1;
        }
        else if (c === "?") {
            unbounded = false;
            end = at + 1;
        }
        else if (c === "{") {
            const m = /^\{(\d+)(,(\d*))?\}/.exec(src.slice(at, at + 24));
            if (!m)
                return null;
            unbounded = m[2] !== undefined && m[3] === "";
            end = at + m[0].length;
        }
        else
            return null;
        if (src[end] === "?")
            end += 1; // lazy
        return { unbounded, end };
    };
    while (i < src.length) {
        const c = src[i];
        if (c === "\\") {
            i += 2;
            const q = quantifier(i);
            if (q) {
                inner ||= q.unbounded;
                i = q.end;
            }
            continue;
        }
        if (c === "[") {
            i += 1;
            while (i < src.length && src[i] !== "]")
                i += src[i] === "\\" ? 2 : 1;
            i += 1;
            const q = quantifier(i);
            if (q) {
                inner ||= q.unbounded;
                i = q.end;
            }
            continue;
        }
        if (c === "(") {
            stack.push(inner);
            inner = false;
            i += 1;
            const prefix = /^\?(?::|=|!|<=|<!|<[A-Za-z_$][\w$]*>)/.exec(src.slice(i, i + 64));
            if (prefix)
                i += prefix[0].length;
            continue;
        }
        if (c === ")") {
            const q = quantifier(i + 1);
            if (inner && q?.unbounded)
                return true;
            const parent = stack.pop() ?? false;
            inner = parent || inner || (q?.unbounded ?? false);
            i = q ? q.end : i + 1;
            continue;
        }
        const q = quantifier(i + 1);
        if (q) {
            inner ||= q.unbounded;
            i = q.end;
            continue;
        }
        i += 1;
    }
    return false;
}
/** A built-in or code-owned pattern, by id AND source: vetted in CI, so not checked again here. */
const TRUSTED = [...BUILTIN_PATTERNS, ...CODE_OWNED_PATTERNS];
const isTrusted = (id, src) => TRUSTED.some((b) => b.id === id && b.regex === src);
/**
 * Compile the patterns once, case-insensitive. Throws BackstopPatternError for the first pattern it
 * refuses: an id that is not an id, a duplicate, a regex that does not compile, one that matches the
 * empty string (it would hit everything), nested unbounded quantifiers ("nested-quantifier"), or —
 * for any pattern that is not trusted (a built-in or code-owned pattern, by id and source) — one
 * outside the pack subset (checkPackPattern): "slow"
 * for an unbounded or too-wide quantifier, too many alternatives or a step bound over budget,
 * "unsupported" for lookaround, a backreference, a nested group or an empty alternative, "invalid"
 * for syntax it does not read, "too-long" past PACK_MAX_SOURCE. Deterministic: no clock is read, so
 * the same patterns are accepted or refused on every machine, however busy.
 */
export function compilePatterns(
// Each entry is meant to be `{ id: string; regex: string }`; atlas and tenant patterns arrive as
// data, so every entry is checked as if it were anything.
patterns, _options = {}) {
    const list = [];
    const seen = new Set();
    for (const p of patterns) {
        const entry = (typeof p === "object" || typeof p === "function") && p !== null ? p : {};
        const id = typeof entry.id === "string" ? entry.id : "";
        if (!ID_RE.test(id))
            throw new BackstopPatternError(id.slice(0, 128), "invalid-id");
        if (seen.has(id))
            throw new BackstopPatternError(id, "duplicate-id");
        seen.add(id);
        const src = entry.regex;
        if (typeof src !== "string" || src.length === 0)
            throw new BackstopPatternError(id, "invalid");
        if (src.length > MAX_SOURCE)
            throw new BackstopPatternError(id, "too-long");
        let re;
        try {
            re = new RegExp(src, "i");
        }
        catch {
            throw new BackstopPatternError(id, "invalid");
        }
        if (re.test(""))
            throw new BackstopPatternError(id, "matches-empty");
        if (!isTrusted(id, src)) {
            if (hasNestedQuantifier(src))
                throw new BackstopPatternError(id, "nested-quantifier");
            const verdict = checkPackPattern(src);
            if (!verdict.ok)
                throw new BackstopPatternError(id, REFUSAL[verdict.rule]);
        }
        list.push({ id, re });
    }
    return Object.freeze({
        ids: Object.freeze(list.map((x) => x.id)),
        list: Object.freeze(list.map((x) => Object.freeze(x))),
    });
}
/** A window may start this much later, and end this much earlier, to fall outside a digit run. */
const ALIGN = 64;
/** A digit or a separator (patterns.ts): a window does not start right after one if it can help it. */
const RUN_CHAR = /[0-9./_:\s-]/;
/**
 * The windows a value is scanned in: the whole value when short, else overlapping slices. A slice
 * that would begin or end inside a run of digits and separators begins up to ALIGN characters later
 * and ends up to ALIGN earlier, outside the run: a pattern's lookarounds cannot see past the slice,
 * so "0/30/2026" or "09/30/202" cut from a date would otherwise read as a seven-digit run. The
 * neighbouring slice covers what is skipped, and slices still overlap by at least SCAN_OVERLAP −
 * 2 × ALIGN = 384 characters, longer than any built-in pattern's match.
 */
function windows(value) {
    if (value.length <= SCAN_WINDOW)
        return [value];
    const out = [];
    const stride = SCAN_WINDOW - SCAN_OVERLAP;
    for (let start = 0; start < value.length; start += stride) {
        let from = start;
        while (from > 0 && from < start + ALIGN && RUN_CHAR.test(value[from - 1] ?? ""))
            from += 1;
        // a run longer than ALIGN: read it as it falls
        if (from === start + ALIGN && RUN_CHAR.test(value[from - 1] ?? ""))
            from = start;
        let to = from + SCAN_WINDOW;
        if (to < value.length) {
            const limit = to - ALIGN;
            while (to > limit && RUN_CHAR.test(value[to] ?? ""))
                to -= 1;
            if (to === limit && RUN_CHAR.test(value[to] ?? ""))
                to = from + SCAN_WINDOW;
        }
        out.push(value.slice(from, to));
        if (to >= value.length)
            break;
    }
    return out;
}
/**
 * Does the value look like something that must not pass? Returns the id of the first pattern, in the
 * order given, that matches anywhere in it — and nothing about the value or where it matched.
 */
export function scan(value, patterns) {
    const raw = patterns?.list;
    if (!Array.isArray(raw) || raw.length === 0)
        return { hit: false };
    const list = raw;
    const text = typeof value === "string"
        ? value
        : typeof value === "number" || typeof value === "bigint"
            ? String(value)
            : "";
    if (text.length === 0)
        return { hit: false };
    const parts = windows(text);
    for (const { id, re } of list) {
        for (const part of parts)
            if (re.test(part))
                return { hit: true, patternId: id };
    }
    return { hit: false };
}
/** The id a name-like value is reported under (D-100); it is not a pattern and never compiled. */
export const NAME_LIKE_ID = "name-like";
const isUpper = (ch) => ch !== ch.toLowerCase() && ch === ch.toUpperCase();
const isLower = (ch) => ch !== ch.toUpperCase() && ch === ch.toLowerCase();
/**
 * D-100: does the value look like a person's name — one capitalised word, such as "Wexley",
 * "O'Neill", "Smith-Jones" or "McAllister"? An initial capital, then lower-case letters, with at
 * most one apostrophe or hyphen joining a second part, or a second capital after Mc or Mac; 2 to 40
 * letters; surrounding whitespace ignored. All capitals ("USD", "OPEN") and lower case are not
 * names. A plain loop, linear in the value and case-sensitive, which a backstop pattern (compiled
 * with the i flag) cannot be. Apply it only to free values: an enum or context value matched against
 * its allowed list is the atlas's word, not the page's, and must not go through it.
 */
export function looksLikeName(value) {
    if (typeof value !== "string" || value.length > 128)
        return false;
    const chars = Array.from(value.trim());
    if (chars.length < 2 || chars.length > 41)
        return false;
    if (!isUpper(chars[0] ?? ""))
        return false;
    let k = 1;
    const lowerRun = () => {
        const from = k;
        while (k < chars.length && isLower(chars[k] ?? ""))
            k += 1;
        return k - from;
    };
    const head = lowerRun();
    if (head > 0 && k === chars.length)
        return true;
    const sep = chars[k] ?? "";
    const apostrophe = sep === "'" || sep === "\u2019";
    if (head === 0 && !apostrophe)
        return false; // O'Neill, D'Arcy: a capital alone before one
    if (apostrophe || sep === "-") {
        k += 1;
        if (isUpper(chars[k] ?? ""))
            k += 1;
    }
    else {
        const prefix = chars.slice(0, k).join("");
        if ((prefix !== "Mc" && prefix !== "Mac") || !isUpper(sep))
            return false;
        k += 1;
    }
    return lowerRun() > 0 && k === chars.length;
}
//# sourceMappingURL=index.js.map